import React, { useEffect, useRef, useState } from 'react';
import useInViewAutoplay from '../../hooks/useInViewAutoplay';
import './TileVideo.css';

// A short muted loop inside a gallery tile - an awning opening, LEDs coming on.
// It fills the tile like a photo (object-fit: cover), plays while on screen
// and pauses off it, and carries one small play/pause button so a visitor who
// asked for reduced motion (no autoplay) can still start it.
//
// preload="none": a tile further down the page costs nothing until it comes
// into view; the poster is all that loads up front.
//
// The parent owns the tile's box (size, radius); this fills it.
export default function TileVideo({ src, poster, label }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  useInViewAutoplay(ref);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    // A decorative loop is not something to cast to a TV: without this,
    // Chrome on Android draws its cast icon over the tile's caption.
    el.disableRemotePlayback = true;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    return () => {
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
    };
  }, []);

  const toggle = () => {
    const el = ref.current;
    if (!el) return;
    if (el.paused) el.play().catch(() => {});
    else el.pause();
  };

  return (
    <>
      <video
        ref={ref}
        className="tile-video"
        src={src}
        poster={poster || undefined}
        muted
        loop
        playsInline
        preload="none"
        width={960}
        height={960}
        aria-label={label}
      />
      <button
        type="button"
        className="tile-video-btn"
        onClick={toggle}
        aria-pressed={playing}
        aria-label={playing ? `Pause video: ${label}` : `Play video: ${label}`}
      >
        {playing ? (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <rect x="6" y="5" width="4" height="14" rx="1" />
            <rect x="14" y="5" width="4" height="14" rx="1" />
          </svg>
        ) : (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M8 5.14v13.72a1 1 0 0 0 1.52.86l11-6.86a1 1 0 0 0 0-1.72l-11-6.86A1 1 0 0 0 8 5.14z" />
          </svg>
        )}
      </button>
    </>
  );
}
